import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, ThumbsDown, ThumbsUp } from "lucide-react";
import PageShell, { Section } from "@/components/site/PageShell";
import { Prose } from "@/components/site/ui";
import Blocks from "@/components/site/Renderer";
import { ICONS } from "@/components/site/icons";
import { ARTICLES, CATEGORIES, findArticle } from "@/content/help";

export function HelpCenter() {
  const [q, setQ] = useState("");
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return null;
    return ARTICLES.filter(a => (a.title + a.short + JSON.stringify(a.sections)).toLowerCase().includes(needle));
  }, [q]);

  return (
    <PageShell eyebrow="Centre d'aide" title="Comment pouvons-nous aider ?"
      lead="Les réponses aux questions que l'on nous pose le plus souvent." centered>
      <Section>
        <div className="help-search">
          <Search size={17} />
          <input
            value={q} onChange={e => setQ(e.target.value)} data-testid="help-search"
            placeholder="Rechercher dans le centre d'aide…" aria-label="Rechercher dans le centre d'aide"
          />
        </div>
      </Section>

      {results ? (
        <Section title={`${results.length} résultat${results.length > 1 ? "s" : ""} pour « ${q} »`}>
          {results.length === 0
            ? <Prose><p>Aucun article ne correspond. Écrivez à <a href="mailto:support@performcustom.com">support@performcustom.com</a> : nous répondrons et ajouterons l'article.</p></Prose>
            : <div className="card-grid cols-2">
                {results.map(a => (
                  <div key={a.slug} className="info-card">
                    <h3>{a.title}</h3><p>{a.short}</p>
                    <Link to={`/aide/${a.slug}`}>Lire<ArrowRight size={12} /></Link>
                  </div>
                ))}
              </div>}
        </Section>
      ) : (
        CATEGORIES.map(cat => {
          const items = ARTICLES.filter(a => a.category === cat.key);
          if (!items.length) return null;
          const Icon = ICONS[cat.icon];
          return (
            <Section key={cat.key} title={cat.label} lead={cat.desc}>
              <div className="help-list">
                {items.map(a => (
                  <Link key={a.slug} to={`/aide/${a.slug}`} className="help-row" data-testid={`help-${a.slug}`}>
                    <span className="help-icon">{Icon && <Icon size={15} />}</span>
                    <div><strong>{a.title}</strong><small>{a.short}</small></div>
                    <ArrowRight size={14} />
                  </Link>
                ))}
              </div>
            </Section>
          );
        })
      )}

      <Section title="Vous ne trouvez pas ?">
        <Prose>
          <p>
            Écrivez à <a href="mailto:support@performcustom.com">support@performcustom.com</a> en heures ouvrées,
            ou consultez la <Link to="/statut">page d'état</Link> si quelque chose semble cassé.
          </p>
        </Prose>
      </Section>
    </PageShell>
  );
}

export function HelpArticle() {
  const { slug } = useParams();
  const article = findArticle(slug);
  const [vote, setVote] = useState(null);
  if (!article) return <Navigate to="/aide" replace />;
  const category = CATEGORIES.find(c => c.key === article.category);
  const related = (article.related || []).map(findArticle).filter(Boolean);

  return (
    <PageShell eyebrow={category?.label} title={article.title} lead={article.short}>
      <Section>
        <Link to="/aide" className="landing-link" style={{ marginBottom: 20, display: "inline-flex" }}>
          <ArrowLeft size={13} />Centre d'aide
        </Link>
        <div className="docs-content" style={{ marginTop: 0 }}>
          {article.sections.map(s => (
            <section key={s.heading}>
              <h2>{s.heading}</h2>
              <Blocks blocks={s.blocks} />
            </section>
          ))}
        </div>
        <p className="help-updated">Dernière mise à jour : {article.updated}.</p>
      </Section>

      <Section>
        <div className="help-feedback" data-testid="help-feedback">
          {vote === null ? (
            <>
              <strong>Cet article vous a-t-il aidé ?</strong>
              <div>
                <button type="button" onClick={() => setVote("yes")} data-testid="help-yes"><ThumbsUp size={14} />Oui</button>
                <button type="button" onClick={() => setVote("no")} data-testid="help-no"><ThumbsDown size={14} />Non</button>
              </div>
            </>
          ) : vote === "yes" ? (
            <strong>Merci, c'est noté.</strong>
          ) : (
            <>
              <strong>Désolé. Dites-nous ce qui manque.</strong>
              <a className="button button-outline" href={`mailto:support@performcustom.com?subject=Retour%20sur%20l%27article%20%22${encodeURIComponent(article.title)}%22`}>Écrire au support</a>
            </>
          )}
        </div>
      </Section>

      {related.length > 0 && (
        <Section title="Articles liés">
          <div className="card-grid cols-2">
            {related.map(a => (
              <div key={a.slug} className="info-card">
                <h3>{a.title}</h3><p>{a.short}</p>
                <Link to={`/aide/${a.slug}`}>Lire<ArrowRight size={12} /></Link>
              </div>
            ))}
          </div>
        </Section>
      )}
    </PageShell>
  );
}
